/**
 * 直传客户端（S07）：签发 → 直传 COS → 校验完成。
 *
 * 三条设计约束（都是踩过的坑）：
 *  1. 浏览器不能拿 secret：只拿服务端签好的**单条 URL**，路径写死在签名里，改不动。
 *  2. 进度用 XMLHttpRequest（fetch 没有 upload.onprogress），并用「已完成分片字节 + 当前片进度」算总进度，
 *     而不是靠分片计数估算——大文件最后一片很小时估算会跳变。
 *  3. 断点续传靠 localStorage 里的会话快照（upload_id + 每片的 URL/etag）。签名有效期 1 小时，
 *     快照过期就丢弃重开新会话（新 key），不做假续传。
 */

import { useCallback, useRef, useState } from "react";
import { callApi, ApiError } from "./api";

export type Purpose = "env_source" | "robot_model" | "dataset" | "policy_ckpt";

export interface SignedPart {
  part_number: number;
  size: number;
  url: string;
  headers: Record<string, string>;
}

export interface SignData {
  upload_id: string;
  mode: "single" | "multipart";
  key: string;
  bucket: string;
  region: string;
  declared_size: number;
  part_size?: number;
  part_count?: number;
  single: { url: string; headers: Record<string, string> } | null;
  parts: SignedPart[] | null;
  expires_at: string;
}

export type UploadPhase = "idle" | "signing" | "uploading" | "verifying" | "done" | "error";

export interface UploadState {
  phase: UploadPhase;
  loaded: number;
  total: number;
  percent: number;
  partDone: number;
  partTotal: number;
  resumed: boolean;
  error?: string;
  uploadId?: string;
}

const IDLE: UploadState = {
  phase: "idle",
  loaded: 0,
  total: 0,
  percent: 0,
  partDone: 0,
  partTotal: 0,
  resumed: false,
};

// ---------------------------------------------------------------------------
// 断点续传快照（按「项目 + 文件名 + 大小 + 修改时间」指纹）
// ---------------------------------------------------------------------------

interface ResumeSnapshot {
  uploadId: string;
  key: string;
  fingerprint: string;
  mode: "multipart";
  partSize: number;
  parts: SignedPart[];
  etags: Record<number, string>;
  expiresAt: string;
}

const KEY_PREFIX = "atc.upload.resume:";

const fingerprintOf = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;

function loadSnapshot(projectId: string, file: File): ResumeSnapshot | null {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + projectId + ":" + fingerprintOf(file));
    if (!raw) return null;
    const s = JSON.parse(raw) as ResumeSnapshot;
    if (s.fingerprint !== fingerprintOf(file)) return null;
    if (new Date(s.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(KEY_PREFIX + projectId + ":" + fingerprintOf(file));
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

function saveSnapshot(projectId: string, snap: ResumeSnapshot) {
  try {
    localStorage.setItem(KEY_PREFIX + projectId + ":" + snap.fingerprint, JSON.stringify(snap));
  } catch {
    /* 配额满/隐私模式：续传失效但上传照常 */
  }
}

function dropSnapshot(projectId: string, fingerprint: string) {
  try {
    localStorage.removeItem(KEY_PREFIX + projectId + ":" + fingerprint);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// 单个 PUT（XHR 才有上传进度）
// ---------------------------------------------------------------------------

function putBlob(
  url: string,
  headers: Record<string, string>,
  blob: Blob,
  onProgress?: (loaded: number) => void
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve((xhr.getResponseHeader("ETag") ?? "").replace(/"/g, ""));
      } else {
        reject(new Error(`直传失败 HTTP ${xhr.status}：${(xhr.responseText ?? "").slice(0, 160)}`));
      }
    };
    xhr.onerror = () => reject(new Error("网络中断（COS 直传失败）"));
    xhr.ontimeout = () => reject(new Error("直传超时"));
    xhr.send(blob);
  });
}

/** 3 次重试，指数退避（断网恢复后能接上）。 */
async function putWithRetry(
  url: string,
  headers: Record<string, string>,
  blob: Blob,
  onProgress?: (loaded: number) => void,
  attempts = 3
): Promise<string> {
  let lastErr: unknown;
  for (let i = 1; i <= attempts; i++) {
    try {
      return await putBlob(url, headers, blob, onProgress);
    } catch (e) {
      lastErr = e;
      if (i < attempts) await new Promise((r) => setTimeout(r, 400 * i));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

// ---------------------------------------------------------------------------
// hook
// ---------------------------------------------------------------------------

export interface UploadOptions {
  projectId: string;
  purpose: Purpose;
  assetId?: string;
  version?: number;
  /** 已有会话（切换页面后继续用同一会话时传） */
  onStateChange?: (s: UploadState) => void;
}

export function useDirectUpload() {
  const [state, setState] = useState<UploadState>(IDLE);
  const cancelRef = useRef(false);

  const update = useCallback((patch: Partial<UploadState>) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      next.percent = next.total > 0 ? Math.min(100, Math.round((next.loaded / next.total) * 100)) : 0;
      return next;
    });
  }, []);

  const cancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  const upload = useCallback(
    async (file: File, opts: UploadOptions): Promise<{ uploadId: string } | null> => {
      cancelRef.current = false;
      const total = file.size;
      setState({ ...IDLE, phase: "signing", total });

      const fingerprint = fingerprintOf(file);
      let snap = loadSnapshot(opts.projectId, file);

      // 已有快照 → 复用它（真正的续传），否则签发新会话
      let data: SignData;
      if (snap) {
        data = {
          upload_id: snap.uploadId,
          mode: "multipart",
          key: snap.key,
          bucket: "",
          region: "",
          declared_size: total,
          part_size: snap.partSize,
          part_count: snap.parts.length,
          single: null,
          parts: snap.parts,
          expires_at: snap.expiresAt,
        };
        update({ resumed: true, partTotal: snap.parts.length, partDone: Object.keys(snap.etags).length });
      } else {
        let signed: SignData;
        try {
          const r = await callApi<SignData>("/api/uploads/sign", {
            method: "POST",
            body: {
              project_id: opts.projectId,
              purpose: opts.purpose,
              filename: file.name,
              size: file.size,
              content_type: file.type || "application/octet-stream",
              asset_id: opts.assetId,
              version: opts.version,
            },
          });
          signed = r.data;
        } catch (e) {
          const msg =
            e instanceof ApiError ? `${e.message}（code=${e.code}）` : (e as Error).message ?? String(e);
          setState((p) => ({ ...p, phase: "error", error: msg }));
          return null;
        }
        data = signed;
        update({
          phase: "uploading",
          uploadId: signed.upload_id,
          partTotal: signed.parts?.length ?? 1,
        });
        if (signed.mode === "multipart" && signed.parts) {
          snap = {
            uploadId: signed.upload_id,
            key: signed.key,
            fingerprint,
            mode: "multipart",
            partSize: signed.part_size ?? 0,
            parts: signed.parts,
            etags: {},
            expiresAt: signed.expires_at,
          };
          saveSnapshot(opts.projectId, snap);
        }
      }

      const etags: Record<number, string> = { ...(snap?.etags ?? {}) };

      try {
        if (data.mode === "single") {
          update({ phase: "uploading", uploadId: data.upload_id, partTotal: 1, partDone: 0 });
          const etag = await putWithRetry(data.single!.url, data.single!.headers, file, (loaded) =>
            update({ loaded })
          );
          void etag;
          update({ loaded: total, partDone: 1 });
        } else {
          const parts = data.parts ?? [];
          const partSize = data.part_size ?? Math.ceil(total / parts.length);
          let done = 0;
          // 已完成的分片直接跳过（续传的核心）
          for (const p of parts) if (etags[p.part_number]) done++;
          update({ phase: "uploading", partDone: done, loaded: Math.min(total, done * partSize) });

          for (const p of parts) {
            if (cancelRef.current) throw new Error("已取消");
            const start = (p.part_number - 1) * partSize;
            const end = Math.min(total, p.size ? start + p.size : start + partSize);
            const blob = file.slice(start, end);
            const baseLoaded = Math.min(total, start);
            if (etags[p.part_number]) {
              continue;
            }
            const etag = await putWithRetry(p.url, p.headers, blob, (loaded) =>
              update({ loaded: baseLoaded + loaded })
            );
            etags[p.part_number] = etag;
            done++;
            update({ partDone: done, loaded: end });
            if (snap) {
              snap.etags = etags;
              saveSnapshot(opts.projectId, snap);
            }
          }
        }

        update({ phase: "verifying", loaded: total });
        const body =
          data.mode === "multipart"
            ? {
                upload_id: data.upload_id,
                parts: Object.entries(etags).map(([n, etag]) => ({ part_number: Number(n), etag })),
              }
            : { upload_id: data.upload_id };
        const done = await callApi<{ id: string }>("/api/uploads/complete", { method: "POST", body });
        dropSnapshot(opts.projectId, fingerprint);
        update({ phase: "done", loaded: total, percent: 100 });
        return { uploadId: done.data.id };
      } catch (e) {
        const msg = e instanceof ApiError ? `${e.message}（code=${e.code}）` : (e as Error).message ?? String(e);
        setState((p) => ({ ...p, phase: "error", error: msg }));
        return null;
      }
    },
    [update]
  );

  return { state, upload, cancel, reset: () => setState(IDLE) };
}
